import { type ResultsSort } from '@atlassian/search-client/results-sort';
import { type ProductEdition } from '@atlassian/search-experiment/dynamic/types';
import { type ExperimentConfig } from '@atlassian/search-experiment/types';

import {
	ALL_PRODUCTS_STRING,
	ARCHIVED,
	CROSSPRODUCT_SEARCH_API,
	CURRENT,
	OptionIds,
} from '../../common/constants/analytics';
import { type ApplicationModes } from '../../common/constants/application-modes';
import { is3pProductKey, type PrimaryProductKey } from '../../common/constants/product-keys';
import {
	isRovoJourneyIdEnabled,
	normalizeRovoJourneyId,
} from '../../common/utils/quick-find/rovo-journey-id';
import { sha256Hash } from '../../common/utils/events/sha256-hash';
import { type CommonAttributes, type FilterAttributes } from '../../common/utils/events/types';

type GetAttributesType = {
	searchSessionId: string;
	requestId: string | undefined;
	searchQuery: string;
	inputQuery: string;
	filters: FilterAttributes;
	queryUpdatedTime?: number;
	productSelected?: string;
	availableProducts: string[];
	sort: ResultsSort;
	experimentConfig: ExperimentConfig;
	applicationMode: ApplicationModes;
	productEdition?: ProductEdition;
	isCQL?: boolean;
	isLiteMode?: boolean;
	primaryProduct?: PrimaryProductKey | null;
	queryParams?: Record<string, any>;
};

export const getAttributes: ({
	searchSessionId,
	requestId,
	searchQuery,
	inputQuery,
	filters,
	queryUpdatedTime,
	productSelected,
	availableProducts,
	sort,
	experimentConfig,
	applicationMode,
	productEdition,
	isCQL,
	isLiteMode,
	primaryProduct,
	queryParams,
}: GetAttributesType) => {
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: {
		query: string;
		inputQuery: string;
	};
} = ({
	searchSessionId,
	requestId,
	searchQuery,
	inputQuery,
	filters,
	queryUpdatedTime,
	productSelected,
	availableProducts,
	sort,
	experimentConfig,
	applicationMode,
	productEdition,
	isCQL,
	isLiteMode,
	primaryProduct,
	queryParams,
}: GetAttributesType) => {
	const { filterSelections, filters: filterCategories } = filters;
	const rovoJourneyId = isRovoJourneyIdEnabled(primaryProduct ?? undefined)
		? normalizeRovoJourneyId(queryParams?.rovoJourneyId)
		: undefined;

	const has3pProducts = !!availableProducts.find((product) => is3pProductKey(product));
	const { frontendExperiments = {} } = experimentConfig;
	const is3pSearch = !!(
		(has3pProducts && !productSelected) ||
		(productSelected && is3pProductKey(productSelected))
	);

	const archivedStatus = filterSelections.some(
		(selection) => selection.value === OptionIds.ARCHIVED,
	)
		? [CURRENT, ARCHIVED]
		: [CURRENT];

	const commonAttributes: CommonAttributes = {
		searchSessionId,
		traceId: requestId,
		productSelected: productSelected || ALL_PRODUCTS_STRING,
		availableProducts,
		mode: applicationMode,
		has3pProducts,
		is3pSearch,
		numberOfTerms: (searchQuery.match(/\w+/g) || []).length,
		queryLength: searchQuery.length,
		inputQueryLength: inputQuery.length,
		queryHash: sha256Hash(searchQuery),
		filters: filterCategories,
		filterSelections,
		containerStatuses: archivedStatus,
		contentStatuses: archivedStatus,
		productEdition,
		frontendExperiments: {
			activeExperiments: Object.keys(frontendExperiments).filter(
				(experiment) => frontendExperiments[experiment]?.serveExperiment,
			),
			...frontendExperiments,
		},
		version: 2,
		api: CROSSPRODUCT_SEARCH_API,
		wordCount: (searchQuery.match(/\w+/g) || []).length,
		isCQL: isCQL ?? false,
		queryUpdatedAgo: queryUpdatedTime ? Date.now() - queryUpdatedTime : undefined,
		sort,
		isSearchPageRelay: true,
		isLiteMode: isLiteMode ?? false,
		...(rovoJourneyId ? { rovoJourneyId } : {}),
	};

	const nonPrivacySafeAttributes = {
		query: searchQuery,
		inputQuery: inputQuery,
	};

	return { commonAttributes, nonPrivacySafeAttributes };
};
